import { useState, useEffect } from 'react'
import {
  Card, Input, Typography, Space, Pagination, Button, Tag, Empty, Spin, Statistic, Row, Col,
  Form, message, Drawer
} from 'antd'
import { SearchOutlined, ArrowLeftOutlined, DatabaseOutlined, FileTextOutlined, EditOutlined } from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import _ from 'lodash'

const { Title, Text } = Typography
const { Search, TextArea } = Input
// const { Option } = Select

interface TrainingExample {
  id: string | number
  line_number: number
  table_name?: string
  system_prompt?: string
  user_message: string
  assistant_response: string
  highlight?: string
}

const DatasetSearchPage = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [dataset, setDataset] = useState<any>({})
  const [searchQuery, setSearchQuery] = useState('')
  // const [filterTable, setFilterTable] = useState('')
  const [examples, setExamples] = useState<TrainingExample[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const [loading, setLoading] = useState(false)
  // const [tables, setTables] = useState<string[]>([])
  const [mode, setMode] = useState<'browse' | 'search'>('browse')
  const [editDrawerVisible, setEditDrawerVisible] = useState(false)
  const [editingExample, setEditingExample] = useState<TrainingExample | null>(null)
  const [form] = Form.useForm()

  useEffect(() => {
    loadDataset()
    loadSampleExamples() // Load examples immediately
  }, [id])

  const loadDataset = async () => {
    try {
      const response = await axios.get(`http://localhost:5000/api/schema/datasets`)
      const foundDataset = response.data.datasets.find((d: any) => d.id === id)
      if (foundDataset) {
        setDataset(foundDataset)
      }
    } catch (error) {
      message.error('Failed to load dataset')
    }
  }

  const loadSampleExamples = async (page = 1) => {
    setLoading(true)
    try {
      const offset = (page - 1) * 10
      const response = await axios.get(
        `http://localhost:5000/api/schema/datasets/${id}/sample`,
        { params: { limit: 10, offset } }
      )

      setExamples(response.data.sample_examples || [])
      setTotalResults(response.data.total_examples || 0)
      setTotalPages(Math.ceil((response.data.total_examples || 0) / 10))
      setMode('browse')
    } catch (error) {
      console.error('Error loading examples:', error)
      message.error('Failed to load examples')
    } finally {
      setLoading(false)
    }
  }

  const search = _.debounce(async (query: string, page = 1) => {
    if (!query.trim()) {
      loadSampleExamples(1)
      return
    }

    setLoading(true)
    setMode('search')

    try {
      const offset = (page - 1) * 10
      const response = await axios.get(
        `http://localhost:5000/api/schema/datasets/${id}/search`,
        { params: { q: query, limit: 10, offset } }
      )

      setExamples(response.data.results || [])
      setTotalResults(response.data.total || 0)
      setTotalPages(response.data.pages || 0)
    } catch (error) {
      message.error('Search failed')
    } finally {
      setLoading(false)
    }
  }, 500)

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    setCurrentPage(1)
    search(value, 1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    if (mode === 'search' && searchQuery) {
      search(searchQuery, page)
    } else {
      loadSampleExamples(page)
    }
  }

  const handleEditExample = (example: TrainingExample) => {
    setEditingExample(example)
    form.setFieldsValue({
      user_message: example.user_message,
      assistant_response: example.assistant_response
    })
    setEditDrawerVisible(true)
  }

  const handleSaveEdit = async () => {
    try {
      const values = await form.validateFields()

      await axios.put(
        `http://localhost:5000/api/schema/datasets/${id}/examples/${editingExample?.id}`,
        {
          user_message: values.user_message,
          assistant_response: values.assistant_response,
          system_prompt: editingExample?.system_prompt || ''
        }
      )

      message.success('Example updated successfully!')
      setEditDrawerVisible(false)

      // Reload current page
      if (mode === 'search' && searchQuery) {
        search(searchQuery, currentPage)
      } else {
        loadSampleExamples(currentPage)
      }
    } catch (error: any) {
      message.error(error.response?.data?.detail || 'Failed to update example')
    }
  }

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <Card style={{ marginBottom: '16px' }}>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/training/datasets/list')}
            type="link"
            style={{ padding: 0 }}
          >
            Back to Datasets
          </Button>

          <Title level={3} style={{ margin: 0 }}>{dataset.name || 'Dataset'}</Title>
          <Text type="secondary">{dataset.description}</Text>

          <Row gutter={16} style={{ marginTop: '12px' }}>
            <Col span={8}>
              <Statistic title="Tables" value={dataset.total_tables || 0} prefix={<DatabaseOutlined />} />
            </Col>
            <Col span={8}>
              <Statistic title="Examples" value={dataset.total_examples || 0} prefix={<FileTextOutlined />} />
            </Col>
          </Row>
        </Space>
      </Card>

      {/* Search */}
      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16}>
          <Col flex="auto">
            <Search
              placeholder="Search training examples... (e.g., 'work order status', 'SELECT', etc.)"
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onSearch={(value) => handleSearchChange(value)}
            />
          </Col>
        </Row>
      </Card>

      {/* Pagination at Top */}
      {totalPages > 1 && (
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Pagination
            current={currentPage}
            total={totalResults}
            pageSize={10}
            onChange={handlePageChange}
            showSizeChanger={false}
            showTotal={(total) => `${total.toLocaleString()} total`}
            size="default"
          />
        </div>
      )}

      {/* Results */}
      {loading ? (
        <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '12px' }}>
            <Text>Loading examples...</Text>
          </div>
        </Card>
      ) : examples.length > 0 ? (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          {examples.map((example: TrainingExample, index: number) => (
            <Card
              key={example.id || index}
              size="small"
              hoverable
              style={{
                borderLeft: '3px solid #1890ff'
              }}
              bodyStyle={{ padding: '12px' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center' }}>
                <Space size="small">
                  {example.table_name && (
                    <Tag color="blue" style={{ fontSize: '11px', margin: 0 }}>
                      <DatabaseOutlined style={{ marginRight: '4px' }} />
                      {example.table_name}
                    </Tag>
                  )}
                  <Text type="secondary" style={{ fontSize: '11px' }}>
                    Line {example.line_number}
                  </Text>
                </Space>

                <Button
                  type="link"
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleEditExample(example)}
                >
                  Edit
                </Button>
              </div>

              <div style={{ marginBottom: '8px' }}>
                <Text strong style={{ color: '#1890ff', fontSize: '12px' }}>Question:</Text>
                <div style={{
                  marginTop: '4px',
                  fontSize: '13px',
                  lineHeight: '1.4'
                }}>
                  {mode === 'search' && example.highlight ? (
                    <span dangerouslySetInnerHTML={{ __html: example.highlight }} />
                  ) : (
                    example.user_message
                  )}
                </div>
              </div>

              <div>
                <Text strong style={{ color: '#52c41a', fontSize: '12px' }}>SQL Answer:</Text>
                <pre style={{
                  background: '#f5f5f5',
                  padding: '8px',
                  borderRadius: '4px',
                  margin: '4px 0 0 0',
                  fontSize: '12px',
                  lineHeight: '1.4',
                  overflow: 'auto',
                  fontFamily: 'Monaco, Consolas, monospace'
                }}>
                  {example.assistant_response}
                </pre>
              </div>
            </Card>
          ))}
        </Space>
      ) : (
        <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Empty
            description={
              mode === 'search'
                ? 'No results found. Try a different search term.'
                : 'No training examples found in this dataset.'
            }
          />
        </Card>
      )}

      {/* Pagination at Bottom */}
      {totalPages > 1 && examples.length > 0 && (
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Pagination
            current={currentPage}
            total={totalResults}
            pageSize={10}
            onChange={handlePageChange}
            showSizeChanger={false}
            showTotal={(total) => `${total.toLocaleString()} total`}
            size="default"
          />
        </div>
      )}

      {/* Edit Drawer */}
      <Drawer
        title={`Edit Example #${editingExample?.line_number}`}
        placement="right"
        width={600}
        onClose={() => setEditDrawerVisible(false)}
        open={editDrawerVisible}
        extra={
          <Space>
            <Button onClick={() => setEditDrawerVisible(false)}>Cancel</Button>
            <Button type="primary" onClick={handleSaveEdit}>
              Save Changes
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Question (User Message)"
            name="user_message"
            rules={[{ required: true, message: 'Please enter a question' }]}
          >
            <TextArea rows={4} placeholder="What does the user ask?" />
          </Form.Item>

          <Form.Item
            label="SQL Answer (Assistant Response)"
            name="assistant_response"
            rules={[{ required: true, message: 'Please enter SQL answer' }]}
          >
            <TextArea
              rows={8}
              placeholder="SQL query response"
              style={{ fontFamily: 'Monaco, Consolas, monospace', fontSize: '13px' }}
            />
          </Form.Item>
        </Form>
      </Drawer>
    </div>
  )
}

export default DatasetSearchPage
